import {
    Accordion,
    Badge,
    Button,
    Checkbox,
    Divider,
    Group,
    Loader,
    NumberInput,
    Paper,
    SegmentedControl,
    Select,
    Stack,
    Text,
    TextInput,
    Title,
} from '@mantine/core';
import { IconCheck, IconSearch, IconTrash, IconUpload } from '@tabler/icons-react';
import { useEffect, useRef, useState, type FC } from 'react';

import { ReparoApi } from '../../../api';

import type {
    AvailableChannel,
    ChannelWarehouseOptions,
    DistributionDemandSource,
    DistributionLeadTimeMode,
    DistributionPath,
    LeadTimeMeasurement,
    LeadTimeScope,
    LeadTimeSuggestion,
    WarehouseDohRule,
} from '@api/distribution';
import {
    deleteWarehouseDohRule,
    getChannelWarehouseOptions,
    getDistributionChannels,
    getDistributionPaths,
    getLeadTimeSuggestions,
    listWarehouseDohRules,
    upsertDistributionChannelConfig,
    upsertDistributionPaths,
    upsertWarehouseDohRule,
} from '@api/distribution';
import {
    getChannelFulfillmentModes,
    setChannelFulfillmentMode,
    type ChannelFulfillmentMode,
    type ChannelFulfillmentModeEntry,
    type ChannelInTransitSource,
} from '@api/channel';
import { showError } from '@utils/notifications';

// ─── Types ────────────────────────────────────────────────────────────────────

interface PathDraftRow {
    fromWarehouseId: string;
    toWarehouseId: string | null;
    leadTimeDays: number | '';
    leadTimeMode: DistributionLeadTimeMode;
}

interface NewPathRow {
    fromWarehouseId: string;
    toWarehouseId: string;
    leadTimeDays: number | '';
}

/** A group of warehouses sharing the same DoH config */
interface RuleGroup {
    warehouseIds: string[];
    dohMode: 'flat' | 'tier';
    targetDohDays: number | '';
    tierDoh: Record<string, number | ''>;
}

function buildRuleGroups(rules: WarehouseDohRule[]): RuleGroup[] {
    const groupMap = new Map<string, { warehouseIds: string[]; rule: WarehouseDohRule }>();
    for (const rule of rules) {
        const tierSorted = rule.tierTargetDohDays
            ? Object.fromEntries(Object.entries(rule.tierTargetDohDays).sort())
            : null;
        const key = JSON.stringify({ t: rule.targetDohDays, tier: tierSorted });
        const entry = groupMap.get(key);
        if (entry) {
            entry.warehouseIds.push(rule.warehouseId);
        } else {
            groupMap.set(key, { warehouseIds: [rule.warehouseId], rule });
        }
    }
    return Array.from(groupMap.values()).map(g => ({
        warehouseIds: g.warehouseIds,
        dohMode: g.rule.tierTargetDohDays ? 'tier' : 'flat',
        targetDohDays: g.rule.targetDohDays ?? '',
        tierDoh: Object.fromEntries(
            Object.entries(g.rule.tierTargetDohDays ?? {}).map(([k, v]) => [k, (v as number | null) ?? '']),
        ),
    }));
}

// ─── Channel detail panel ─────────────────────────────────────────────────────

interface ChannelDetailProps {
    channel: AvailableChannel;
    /** Channel fulfilment mode — velocity source is only relevant (and required) for PO-driven. */
    fulfillmentMode: ChannelFulfillmentMode;
    onConfigSaved: (updated: AvailableChannel) => void;
}

const extractErrorMessage = (err: unknown): string => {
    const data = (err as any)?.error?.data;
    if (data) {
        const msg = data.message;
        if (Array.isArray(msg) && msg.length > 0) {
            const first = msg[0];
            if (first?.constraints) return Object.values(first.constraints).join(', ');
        }
        if (typeof msg === 'string') return msg;
    }
    return (err as any)?.error?.message ?? (err instanceof Error ? err.message : 'Save failed');
};

/** Section header inside the config form */
const SectionTitle: FC<{ children: string }> = ({ children }) => (
    <Text size="xs" fw={700} tt="uppercase" c="dimmed" style={{ letterSpacing: '0.05em' }}>
        {children}
    </Text>
);

/** Inline warehouse multi-picker — avoids portal/positioning issues inside Modals */
interface WhPickerProps {
    label: string;
    options: { value: string; label: string }[];
    value: string[];
    onChange: (ids: string[]) => void;
    disabled?: boolean;
}

const WhPicker: FC<WhPickerProps> = ({ label, options, value, onChange, disabled }) => {
    const [search, setSearch] = useState('');
    const filtered = search
        ? options.filter(o => o.label.toLowerCase().includes(search.toLowerCase()))
        : options;
    const isAllSelected = options.length > 0 && options.every(o => value.includes(o.value));
    const isSomeSelected = value.length > 0 && !isAllSelected;

    const toggle = (id: string) =>
        onChange(value.includes(id) ? value.filter(v => v !== id) : [...value, id]);

    const toggleAll = () => onChange(isAllSelected ? [] : options.map(o => o.value));

    return (
        <Stack gap={4}>
            <Group justify="space-between" align="center">
                <Text size="sm" fw={500}>{label}</Text>
                <Text size="xs" c="dimmed">
                    {isAllSelected ? 'All selected' : isSomeSelected ? `${value.length} / ${options.length} selected` : 'None selected'}
                </Text>
            </Group>
            <div style={{
                border: '1px solid var(--mantine-color-gray-4)',
                borderRadius: 8,
                overflow: 'hidden',
                opacity: disabled ? 0.5 : 1,
                pointerEvents: disabled ? 'none' : undefined,
            }}>
                <div style={{ padding: '6px 8px', borderBottom: '1px solid var(--mantine-color-gray-2)', backgroundColor: 'var(--mantine-color-gray-0)' }}>
                    <TextInput
                        placeholder="Search warehouses…"
                        value={search}
                        onChange={e => setSearch(e.currentTarget.value)}
                        size="xs"
                        leftSection={<IconSearch size={13} />}
                    />
                </div>
                <div style={{ maxHeight: 190, overflowY: 'auto' }}>
                    {!search && (
                        <Checkbox
                            size="xs"
                            label={<Text size="xs" fw={600}>All FCs</Text>}
                            checked={isAllSelected}
                            indeterminate={isSomeSelected}
                            onChange={toggleAll}
                            disabled={options.length === 0}
                            wrapperProps={{ style: { padding: '7px 10px', borderBottom: '1px solid var(--mantine-color-gray-1)', cursor: 'pointer' } }}
                        />
                    )}
                    {filtered.map(o => (
                        <Checkbox
                            key={o.value}
                            size="xs"
                            label={<Text size="xs">{o.label}</Text>}
                            checked={value.includes(o.value)}
                            onChange={() => toggle(o.value)}
                            wrapperProps={{ style: { padding: '5px 10px', cursor: 'pointer' } }}
                        />
                    ))}
                    {filtered.length === 0 && (
                        <Text size="xs" c="dimmed" style={{ padding: '8px 10px' }}>No warehouses match</Text>
                    )}
                </div>
            </div>
        </Stack>
    );
};

/** DoH inputs — flat mode */
const FlatDohInput: FC<{ label?: string; value: number | ''; onChange: (v: number | '') => void }> = ({ label = 'Target DoH', value, onChange }) => (
    <NumberInput
        label={label}
        placeholder="e.g. 30"
        value={value}
        onChange={v => onChange(v === '' ? '' : Number(v))}
        min={1} max={365} suffix="d" size="sm" w={140}
    />
);

/** DoH inputs — tier mode */
const TierDohInputs: FC<{
    tiers: string[];
    values: Record<string, number | ''>;
    onChange: (tier: string, v: number | '') => void;
    defaultDoh: number | '';
    onChangeDefault: (v: number | '') => void;
}> = ({ tiers, values, onChange, defaultDoh, onChangeDefault }) => (
    <Stack gap="xs">
        <Group gap="sm" wrap="wrap">
            {tiers.map(tier => (
                <NumberInput
                    key={tier}
                    label={`Tier ${tier}`}
                    placeholder="e.g. 30"
                    value={values[tier] ?? ''}
                    onChange={v => onChange(tier, v === '' ? '' : Number(v))}
                    min={1} max={365} suffix="d" size="sm" style={{ flex: '1 1 80px' }}
                />
            ))}
        </Group>
        <NumberInput
            label="Fallback DoH (products without a tier)"
            placeholder="e.g. 30"
            value={defaultDoh}
            onChange={v => onChangeDefault(v === '' ? '' : Number(v))}
            min={1} max={365} suffix="d" size="sm" w={260}
        />
    </Stack>
);

const LEAD_TIME_SCOPE_LABEL: Record<LeadTimeScope, string> = {
    PATH: 'this route',
    FC: 'this FC, any source warehouse',
    CHANNEL: 'whole channel',
};

const LeadTimeModeBadge: FC<{ mode: DistributionLeadTimeMode }> = ({ mode }) => (
    <Badge size="xs" variant="light" color={mode === 'AUTO' ? 'teal' : 'gray'}>
        {mode === 'AUTO' ? 'Auto' : 'Manual'}
    </Badge>
);

/** The three cases: total and split measured, total only, or nothing measured. */
const PathTiming: FC<{
    suggestion: LeadTimeSuggestion | undefined;
    row: PathDraftRow;
    prepDays: number;
}> = ({ suggestion, row, prepDays }) => {
    const total = suggestion?.total;
    const typedTotal = row.leadTimeDays === '' ? null : prepDays + Number(row.leadTimeDays);
    if (row.leadTimeMode === 'AUTO' && total) {
        const split = suggestion.lead && suggestion.prep
            ? ` = process ${suggestion.prep.days}d + lead ${suggestion.lead.days}d`
            : '';
        return (
            <Text size="xs" c="dimmed" mt={4}>
                Total {total.days}d{split}, from {total.sample} documents over {total.windowDays} days, {LEAD_TIME_SCOPE_LABEL[total.scope]}
            </Text>
        );
    }
    if (row.leadTimeMode === 'AUTO') {
        return (
            <Text size="xs" c="dimmed" mt={4}>
                Not enough history yet{typedTotal === null ? ', enter a lead time' : `, using M1 + M2 + lead = ${typedTotal}d`}
            </Text>
        );
    }
    return (
        <Text size="xs" c="dimmed" mt={4}>
            {typedTotal === null ? 'Enter a lead time' : `Total ${typedTotal}d (M1 + M2 + lead)`}
            {total ? ` · history says ${total.days}d, ${LEAD_TIME_SCOPE_LABEL[total.scope]}` : ''}
        </Text>
    );
};

const ChannelDetail: FC<ChannelDetailProps> = ({ channel, fulfillmentMode, onConfigSaved }) => {
    const [loading, setLoading] = useState(true);
    const [whOptions, setWhOptions] = useState<ChannelWarehouseOptions | null>(null);

    const isPoDriven = fulfillmentMode === 'PO_DRIVEN';

    // Config state
    const [isAggregated, setIsAggregated] = useState(channel.config?.isAggregated ?? false);
    // Demand signal is derived from the channel's fulfilment mode (Channel settings),
    // not edited here; kept only to preserve the stored push default on save.
    const [demandSource] = useState<DistributionDemandSource>(
        channel.config?.demandSource ?? 'FORECAST',
    );
    // Velocity (rate) source — only for PO-driven channels. Drives the burn-down, lead-time demand
    // and DoH while the PO quantity is the demand target. Required before a PO-driven plan can run.
    const [velocitySource, setVelocitySource] = useState<DistributionDemandSource | null>(
        channel.config?.velocitySource ?? null,
    );
    const [milestone1Days, setMilestone1Days] = useState<number | ''>(channel.config?.milestone1Days ?? 1);
    const [milestone2Days, setMilestone2Days] = useState<number | ''>(channel.config?.milestone2Days ?? 1);
    const [dohMode, setDohMode] = useState<'flat' | 'tier'>(
        channel.config?.tierTargetDohDays != null ? 'tier' : 'flat',
    );
    const [targetDohDays, setTargetDohDays] = useState<number | ''>(channel.config?.targetDohDays ?? '');
    const [tierDohMap, setTierDohMap] = useState<Record<string, number | ''>>(() =>
        Object.fromEntries(Object.entries(channel.config?.tierTargetDohDays ?? {}).map(([k, v]) => [k, v as number | ''])),
    );
    const [minSourceStockUnits, setMinSourceStockUnits] = useState<number | ''>(
        channel.config?.minSourceStockUnits ?? '',
    );
    const [maxOutboundUtilizationPct, setMaxOutboundUtilizationPct] = useState<number | ''>(
        channel.config?.maxOutboundUtilizationPct != null
            ? Math.round(channel.config.maxOutboundUtilizationPct * 100)
            : '',
    );
    const [inboundInTransitDays, setInboundInTransitDays] = useState<number | ''>(
        channel.config?.inboundInTransitDays ?? '',
    );
    const [sourceReserveUnits, setSourceReserveUnits] = useState<number | ''>(
        channel.config?.sourceReserveUnits ?? '',
    );
    const [minInternalDohDays, setMinInternalDohDays] = useState<number | ''>(
        channel.config?.minInternalDohDays ?? '',
    );
    const [maxUnitsPerShipment, setMaxUnitsPerShipment] = useState<number | ''>(
        channel.config?.maxUnitsPerShipment ?? '',
    );
    const [maxMeasurementPerShipment, setMaxMeasurementPerShipment] = useState<number | ''>(
        channel.config?.maxMeasurementPerShipment ?? '',
    );
    const [measurementUnit, setMeasurementUnit] = useState<'weight' | 'volume' | null>(
        channel.config?.measurementUnit ?? null,
    );
    const prepDays = Number(milestone1Days || 0) + Number(milestone2Days || 0);
    const [configSaving, setConfigSaving] = useState(false);
    const [configSaved, setConfigSaved] = useState(false);
    const [configError, setConfigError] = useState<string | null>(null);
    const [orgTiers, setOrgTiers] = useState<string[]>([]);

    // Reset "Saved" indicator whenever the user edits any config field
    const configMounted = useRef(false);
    useEffect(() => {
        if (!configMounted.current) { configMounted.current = true; return; }
        setConfigSaved(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAggregated, demandSource, velocitySource, milestone1Days, milestone2Days, dohMode, targetDohDays,
        JSON.stringify(tierDohMap), minSourceStockUnits, maxOutboundUtilizationPct, inboundInTransitDays,
        sourceReserveUnits, minInternalDohDays,
        maxUnitsPerShipment, maxMeasurementPerShipment, measurementUnit]);

    // Warehouse DoH rule groups (per-warehouse channels)
    const [ruleGroups, setRuleGroups] = useState<RuleGroup[]>([]);
    const [editingGroupIdx, setEditingGroupIdx] = useState<number | null>(null);
    const [editGroupWhs, setEditGroupWhs] = useState<string[]>([]);
    const [editGroupDohMode, setEditGroupDohMode] = useState<'flat' | 'tier'>('flat');
    const [editGroupDoh, setEditGroupDoh] = useState<number | ''>('');
    const [editGroupTierMap, setEditGroupTierMap] = useState<Record<string, number | ''>>({});
    const [addingGroup, setAddingGroup] = useState(false);
    const [newGroupWhs, setNewGroupWhs] = useState<string[]>([]);
    const [newGroupDohMode, setNewGroupDohMode] = useState<'flat' | 'tier'>('flat');
    const [newGroupDoh, setNewGroupDoh] = useState<number | ''>('');
    const [newGroupTierMap, setNewGroupTierMap] = useState<Record<string, number | ''>>({});
    const [groupSaving, setGroupSaving] = useState(false);
    const [groupError, setGroupError] = useState<string | null>(null);

    // Paths state
    const [pathRows, setPathRows] = useState<PathDraftRow[]>([]);
    const [pathsSaving, setPathsSaving] = useState(false);
    const [pathsError, setPathsError] = useState<string | null>(null);
    const [editingPaths, setEditingPaths] = useState(false);
    const [pathsSaved, setPathsSaved] = useState(false);
    const [addingPath, setAddingPath] = useState(false);
    const [newRow, setNewRow] = useState<NewPathRow>({ fromWarehouseId: '', toWarehouseId: '', leadTimeDays: '' });
    const [timingMode, setTimingMode] = useState<DistributionLeadTimeMode>('MANUAL');
    const [windowDays, setWindowDays] = useState(30);
    const [suggestions, setSuggestions] = useState<Map<string, LeadTimeSuggestion>>(new Map());
    const [channelMeasure, setChannelMeasure] = useState<LeadTimeMeasurement | null>(null);
    const csvInputRef = useRef<HTMLInputElement>(null);

    const handleCsvUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || !whOptions) return;
        const validInternalIds = new Set(whOptions.internalWarehouses.map(w => w.warehouseId));
        const validChannelIds = new Set(whOptions.channelWarehouses.map(w => w.warehouseId));
        const reader = new FileReader();
        reader.onload = ev => {
            const lines = (ev.target?.result as string).split('\n').map(l => l.trim()).filter(Boolean);
            const dataLines = lines.length > 0 && isNaN(Number(lines[0].split(',')[2]?.trim())) ? lines.slice(1) : lines;
            const newRows: PathDraftRow[] = [];
            const errors: string[] = [];
            for (const [i, line] of dataLines.entries()) {
                const parts = line.split(',').map(p => p.trim());
                if (parts.length < 3) { errors.push(`Row ${i + 1}: expected 3 columns`); continue; }
                const [fromId, toId, leadRaw] = parts;
                if (!validInternalIds.has(fromId)) { errors.push(`Row ${i + 1}: "${fromId}" is not a valid internal warehouse`); continue; }
                if (!validChannelIds.has(toId)) { errors.push(`Row ${i + 1}: "${toId}" is not a valid channel warehouse`); continue; }
                const lead = parseInt(leadRaw, 10);
                if (isNaN(lead) || lead < 0) { errors.push(`Row ${i + 1}: lead time must be a non-negative integer`); continue; }
                newRows.push({ fromWarehouseId: fromId, toWarehouseId: toId, leadTimeDays: lead, leadTimeMode: timingMode });
            }
            setPathsError(errors.length > 0 ? errors.join('\n') : null);
            if (newRows.length > 0) setPathRows(prev => [...prev, ...newRows]);
        };
        reader.readAsText(file);
        e.target.value = '';
    };

    useEffect(() => {
        ReparoApi<string[]>({ method: 'POST', url: '/products/tiers/list', body: JSON.stringify({}) })
            .then(tiers => setOrgTiers(tiers ?? []))
            .catch(() => {});
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        Promise.all([
            getChannelWarehouseOptions(channel.channelAbbreviationKey),
            getDistributionPaths(channel.channelAbbreviationKey),
            listWarehouseDohRules(channel.channelAbbreviationKey),
            getChannelFulfillmentModes(),
        ])
            .then(([opts, paths, rules, modes]: [ChannelWarehouseOptions, DistributionPath[], WarehouseDohRule[], ChannelFulfillmentModeEntry[]]) => {
                setWhOptions(opts);
                setWindowDays(modes.find(m => m.channelAbbreviationKey === channel.channelAbbreviationKey)?.leadTimeWindowDays ?? 30);
                const rows = buildRows(paths, channel.config?.isAggregated ?? false, opts);
                const savedRows = rows.filter(r => r.leadTimeDays !== '');
                setPathRows(rows);
                setTimingMode(savedRows.length > 0 && savedRows.every(r => r.leadTimeMode === 'AUTO') ? 'AUTO' : 'MANUAL');
                setRuleGroups(buildRuleGroups(rules));
                if (paths.length > 0) setPathsSaved(true);
                if (opts.internalWarehouses[0]) setNewRow(r => ({ ...r, fromWarehouseId: opts.internalWarehouses[0].warehouseId }));
                if (opts.channelWarehouses[0]) setNewRow(r => ({ ...r, toWarehouseId: opts.channelWarehouses[0].warehouseId }));
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const routeKey = (row: { fromWarehouseId: string; toWarehouseId: string | null }) =>
        `${row.fromWarehouseId}::${row.toWarehouseId ?? ''}`;
    const routesKey = pathRows.map(routeKey).join('|');

    // Suggestions are read fresh on open and whenever the routes or the window change; nothing is saved.
    useEffect(() => {
        if (pathRows.length === 0) {
            setSuggestions(new Map());
            setChannelMeasure(null);
            return;
        }
        getLeadTimeSuggestions(
            channel.channelAbbreviationKey,
            pathRows.map(r => ({ fromWarehouseId: r.fromWarehouseId, toWarehouseId: r.toWarehouseId })),
            windowDays,
        )
            .then(res => {
                setSuggestions(new Map(res.suggestions.map(s => [routeKey(s), s])));
                setChannelMeasure(res.channel);
            })
            .catch(() => {
                setSuggestions(new Map());
                setChannelMeasure(null);
            });
    }, [routesKey, windowDays]); // eslint-disable-line react-hooks/exhaustive-deps

    const saveWindow = (value: number) => {
        if (value === windowDays) return;
        setWindowDays(value);
        setChannelFulfillmentMode(channel.channelAbbreviationKey, { leadTimeWindowDays: value })
            .catch(() => showError('Could not save the measurement window.'));
    };

    const buildRows = (paths: DistributionPath[], aggregated: boolean, opts: ChannelWarehouseOptions): PathDraftRow[] => {
        if (aggregated) {
            const byFromWh = new Map(paths.map(p => [p.fromWarehouseId, p]));
            return opts.internalWarehouses.map(wh => {
                const existing = byFromWh.get(wh.warehouseId);
                return { fromWarehouseId: wh.warehouseId, toWarehouseId: null, leadTimeDays: existing?.leadTimeDays ?? '', leadTimeMode: existing?.leadTimeMode ?? 'MANUAL' };
            });
        }
        return paths.map(p => ({ fromWarehouseId: p.fromWarehouseId, toWarehouseId: p.toWarehouseId, leadTimeDays: p.leadTimeDays, leadTimeMode: p.leadTimeMode }));
    };

    const changeTimingMode = (mode: DistributionLeadTimeMode) => {
        setTimingMode(mode);
        setPathRows(rows => rows.map(r => ({ ...r, leadTimeMode: mode })));
    };

    const handleModeChange = (newAggregated: boolean) => {
        setIsAggregated(newAggregated);
        setAddingPath(false);
        if (whOptions) {
            if (newAggregated) {
                setPathRows(whOptions.internalWarehouses.map(wh => ({ fromWarehouseId: wh.warehouseId, toWarehouseId: null, leadTimeDays: '', leadTimeMode: timingMode })));
            } else {
                setPathRows([]);
            }
        }
    };

    // ── Config save ──────────────────────────────────────────────────────────────

    const saveConfig = async (): Promise<boolean> => {
        setConfigSaving(true);
        setConfigError(null);
        try {
            let tierDohPayload: Record<string, number> | null = null;
            const flatDohPayload: number | null = targetDohDays === '' ? null : Number(targetDohDays);
            if (dohMode === 'tier') {
                const tierMap: Record<string, number> = {};
                for (const [tier, val] of Object.entries(tierDohMap)) {
                    if (val !== '') tierMap[tier] = Number(val);
                }
                tierDohPayload = Object.keys(tierMap).length > 0 ? tierMap : null;
            }
            const minSourcePayload = minSourceStockUnits === '' ? null : Number(minSourceStockUnits);
            const maxUtilPayload = maxOutboundUtilizationPct === '' ? null : Number(maxOutboundUtilizationPct) / 100;
            const inboundInTransitPayload = inboundInTransitDays === '' ? 0 : Number(inboundInTransitDays);
            const sourceReservePayload = sourceReserveUnits === '' ? null : Number(sourceReserveUnits);
            const minInternalDohPayload = minInternalDohDays === '' ? null : Number(minInternalDohDays);
            const maxUnitsPayload = maxUnitsPerShipment === '' ? null : Number(maxUnitsPerShipment);
            const maxMeasurementPayload = maxMeasurementPerShipment === '' ? null : Number(maxMeasurementPerShipment);
            await upsertDistributionChannelConfig(channel.channelAbbreviationKey, {
                isAggregated,
                demandSource,
                // Persist velocity only for PO-driven channels; clear it otherwise.
                velocitySource: isPoDriven ? velocitySource : null,
                milestone1Days: milestone1Days === '' ? undefined : Number(milestone1Days),
                milestone2Days: milestone2Days === '' ? undefined : Number(milestone2Days),
                targetDohDays: dohMode === 'flat' ? flatDohPayload : null,
                tierTargetDohDays: dohMode === 'tier' ? tierDohPayload : null,
                minSourceStockUnits: minSourcePayload,
                sourceReserveUnits: sourceReservePayload,
                minInternalDohDays: minInternalDohPayload,
                maxOutboundUtilizationPct: maxUtilPayload,
                inboundInTransitDays: inboundInTransitPayload,
                maxUnitsPerShipment: maxUnitsPayload,
                maxMeasurementPerShipment: maxMeasurementPayload,
                measurementUnit: measurementUnit,
            });
            onConfigSaved({
                ...channel,
                config: {
                    ...(channel.config ?? { id: '', createdAt: '', updatedAt: '', horizonDays: null, targetDohDays: null, tierTargetDohDays: null, minSourceStockUnits: null, sourceReserveUnits: null, minInternalDohDays: null, maxOutboundUtilizationPct: null, inboundInTransitDays: 0, maxUnitsPerShipment: null, maxMeasurementPerShipment: null, measurementUnit: null, emailConfig: null }),
                    channelAbbreviationKey: channel.channelAbbreviationKey,
                    isAggregated,
                    demandSource,
                    velocitySource: isPoDriven ? velocitySource : null,
                    milestone1Days: milestone1Days === '' ? 1 : Number(milestone1Days),
                    milestone2Days: milestone2Days === '' ? 1 : Number(milestone2Days),
                    targetDohDays: dohMode === 'flat' ? flatDohPayload : null,
                    tierTargetDohDays: dohMode === 'tier' ? tierDohPayload : null,
                    minSourceStockUnits: minSourcePayload,
                    sourceReserveUnits: sourceReservePayload,
                    minInternalDohDays: minInternalDohPayload,
                    maxOutboundUtilizationPct: maxUtilPayload,
                    inboundInTransitDays: inboundInTransitPayload,
                    maxUnitsPerShipment: maxUnitsPayload,
                    maxMeasurementPerShipment: maxMeasurementPayload,
                    measurementUnit: measurementUnit,
                },
            });
            setConfigSaved(true);
            return true;
        } catch (err: unknown) {
            setConfigError(extractErrorMessage(err));
            return false;
        } finally {
            setConfigSaving(false);
        }
    };

    // ── Rule group helpers ────────────────────────────────────────────────────────

    const reloadRuleGroups = async () => {
        const rules = await listWarehouseDohRules(channel.channelAbbreviationKey);
        setRuleGroups(buildRuleGroups(rules));
    };

    const persistRuleGroup = async (warehouseIds: string[], form: { dohMode: 'flat' | 'tier'; targetDohDays: number | ''; tierDoh: Record<string, number | ''> }, oldWarehouseIds?: string[]) => {
        const flatPayload = form.targetDohDays === '' ? null : Number(form.targetDohDays);
        let tierPayload: Record<string, number> | null = null;
        if (form.dohMode === 'tier') {
            const m: Record<string, number> = {};
            for (const [tier, val] of Object.entries(form.tierDoh)) {
                if (val !== '') m[tier] = Number(val);
            }
            tierPayload = Object.keys(m).length > 0 ? m : null;
        }
        // Delete rules for warehouses removed from this group
        const removed = (oldWarehouseIds ?? []).filter(id => !warehouseIds.includes(id));
        if (removed.length > 0) {
            await Promise.all(removed.map(id => deleteWarehouseDohRule(channel.channelAbbreviationKey, id)));
        }
        await Promise.all(warehouseIds.map(id => upsertWarehouseDohRule(channel.channelAbbreviationKey, id, {
            targetDohDays: flatPayload,
            tierTargetDohDays: tierPayload,
        })));
    };

    const saveNewGroup = async () => {
        if (newGroupWhs.length === 0) return;
        setGroupSaving(true);
        setGroupError(null);
        try {
            await persistRuleGroup(newGroupWhs, { dohMode: newGroupDohMode, targetDohDays: newGroupDoh, tierDoh: newGroupTierMap });
            await reloadRuleGroups();
            setAddingGroup(false);
            setNewGroupWhs([]);
            setNewGroupDoh('');
            setNewGroupTierMap({});
        } catch (err) {
            setGroupError(extractErrorMessage(err));
        } finally {
            setGroupSaving(false);
        }
    };

    const saveEditedGroup = async (idx: number) => {
        if (editGroupWhs.length === 0) return;
        setGroupSaving(true);
        setGroupError(null);
        try {
            const old = ruleGroups[idx];
            await persistRuleGroup(editGroupWhs, { dohMode: editGroupDohMode, targetDohDays: editGroupDoh, tierDoh: editGroupTierMap }, old.warehouseIds);
            await reloadRuleGroups();
            setEditingGroupIdx(null);
        } catch (err) {
            setGroupError(extractErrorMessage(err));
        } finally {
            setGroupSaving(false);
        }
    };

    const deleteGroup = async (group: RuleGroup) => {
        try {
            await Promise.all(group.warehouseIds.map(id => deleteWarehouseDohRule(channel.channelAbbreviationKey, id)));
            await reloadRuleGroups();
        } catch {
            setGroupError('Failed to delete rule.');
        }
    };

    const startEditGroup = (idx: number) => {
        const g = ruleGroups[idx];
        setEditingGroupIdx(idx);
        setEditGroupWhs([...g.warehouseIds]);
        setEditGroupDohMode(g.dohMode);
        setEditGroupDoh(g.targetDohDays);
        setEditGroupTierMap({ ...g.tierDoh });
        setGroupError(null);
    };

    // ── Paths save ────────────────────────────────────────────────────────────────

    // The transit number still ages out dispatched stock, so an AUTO path saved with only a total gets
    // what is left of it. A blank aggregated row means that warehouse has no path, so it stays blank.
    const leadToSave = (row: PathDraftRow): number | '' => {
        const total = suggestions.get(routeKey(row))?.total;
        if (row.leadTimeDays !== '' || row.leadTimeMode !== 'AUTO' || isAggregated || !total) return row.leadTimeDays;
        return Math.max(total.days - prepDays, 0);
    };

    const savePaths = async () => {
        setPathsSaving(true);
        setPathsError(null);
        try {
            if (!(await saveConfig())) return;
            const valid = pathRows
                .map(r => ({ ...r, leadTimeDays: leadToSave(r) }))
                .filter(r => r.leadTimeDays !== '' && r.fromWarehouseId)
                .map(r => ({ fromWarehouseId: r.fromWarehouseId, toWarehouseId: r.toWarehouseId, leadTimeDays: Number(r.leadTimeDays), leadTimeMode: r.leadTimeMode, isActive: true }));
            await upsertDistributionPaths(channel.channelAbbreviationKey, valid);
            onConfigSaved({ ...channel, pathCount: valid.length });
            setEditingPaths(false);
            setAddingPath(false);
            setPathsSaved(true);
        } catch (err: unknown) {
            setPathsError(extractErrorMessage(err));
        } finally {
            setPathsSaving(false);
        }
    };

    const deleteRow = (idx: number) => setPathRows(rows => rows.filter((_, i) => i !== idx));

    const confirmAddRow = () => {
        if (!newRow.fromWarehouseId) return;
        setPathRows(rows => [...rows, { fromWarehouseId: newRow.fromWarehouseId, toWarehouseId: isAggregated ? null : newRow.toWarehouseId || null, leadTimeDays: newRow.leadTimeDays, leadTimeMode: timingMode }]);
        setAddingPath(false);
        setNewRow({ fromWarehouseId: whOptions?.internalWarehouses[0]?.warehouseId ?? '', toWarehouseId: whOptions?.channelWarehouses[0]?.warehouseId ?? '', leadTimeDays: '' });
    };

    if (loading) return <Loader size="sm" mt="md" />;

    const needsProcessTime = timingMode === 'MANUAL' || pathRows.some(r => !suggestions.get(routeKey(r))?.total);

    const internalOptions = (whOptions?.internalWarehouses ?? []).map(w => ({ value: w.warehouseId, label: w.name }));
    const channelWhOptions = (whOptions?.channelWarehouses ?? []).map(w => ({ value: w.warehouseId, label: w.name }));
    const whNameById = new Map([
        ...(whOptions?.internalWarehouses ?? []).map(w => [w.warehouseId, w.name] as [string, string]),
        ...(whOptions?.channelWarehouses ?? []).map(w => [w.warehouseId, w.name] as [string, string]),
    ]);

    // Warehouses already assigned to a rule group (for "add new group" exclusion)
    const assignedWarehouseIds = new Set(ruleGroups.flatMap(g => g.warehouseIds));
    // When editing, exclude warehouses in OTHER groups (not current)
    const editingGroupWhs = editingGroupIdx != null ? new Set(ruleGroups[editingGroupIdx].warehouseIds) : new Set<string>();
    const warehousesForNewGroup = channelWhOptions.filter(w => !assignedWarehouseIds.has(w.value));
    const warehousesForEditGroup = (idx: number) => {
        const otherGroupIds = new Set(ruleGroups.flatMap((g, i) => i === idx ? [] : g.warehouseIds));
        return channelWhOptions.filter(w => !otherGroupIds.has(w.value));
    };

    const formatGroupDoh = (g: RuleGroup) => {
        if (g.dohMode === 'tier') {
            const parts = Object.entries(g.tierDoh)
                .sort(([a], [b]) => a.localeCompare(b))
                .filter(([, v]) => v !== '')
                .map(([tier, v]) => `${tier}: ${v}d`);
            if (g.targetDohDays !== '') parts.push(`default: ${g.targetDohDays}d`);
            return parts.join(' · ') || '—';
        }
        return g.targetDohDays !== '' ? `${g.targetDohDays}d` : '—';
    };

    return (
        <Stack gap={0} mt="sm">
            {/* ── Channel config — collapsible sections ──────────────── */}
            <Accordion multiple defaultValue={['routing', 'demand', 'doh']} variant="separated" radius="md">
                {/* Routing Mode */}
                <Accordion.Item value="routing">
                    <Accordion.Control>
                        <Group gap="xs">
                            <Text size="sm" fw={600}>Routing Mode</Text>
                            <Badge size="xs" variant="light" color={isAggregated ? 'blue' : 'grape'}>
                                {isAggregated ? 'Aggregate to channel' : 'Per FC'}
                            </Badge>
                        </Group>
                    </Accordion.Control>
                    <Accordion.Panel>
                        <Stack gap="xs">
                            <SegmentedControl
                                value={isAggregated ? 'aggregate' : 'per-warehouse'}
                                onChange={v => handleModeChange(v === 'aggregate')}
                                data={[
                                    { value: 'per-warehouse', label: 'Per FC' },
                                    { value: 'aggregate', label: 'Aggregate to channel' },
                                ]}
                                size="sm"
                            />
                            <Text size="xs" c="dimmed">
                                {isAggregated
                                    ? 'Stock moves to the channel as a whole — the channel handles internal distribution.'
                                    : 'Stock is directed to specific warehouses within the channel.'}
                            </Text>
                        </Stack>
                    </Accordion.Panel>
                </Accordion.Item>

                {/* Demand signal is governed by the channel's fulfilment mode (set in Channel
                    settings): PO-driven → Open POs; Push → chosen per plan. No selector here.
                    PO-driven channels still need a velocity (rate) source for DoH / lead-time. */}
                {isPoDriven && (
                    <Accordion.Item value="velocity">
                        <Accordion.Control>
                            <Group gap="xs">
                                <Text size="sm" fw={600}>Velocity source</Text>
                                {velocitySource
                                    ? (
                                        <Badge size="xs" variant="light" color="grape">
                                            {velocitySource === 'FORECAST' ? 'Forecast' : 'Past sales'}
                                        </Badge>
                                    )
                                    : <Badge size="xs" variant="light" color="red">Required</Badge>}
                            </Group>
                        </Accordion.Control>
                        <Accordion.Panel>
                            <Stack gap="xs">
                                <Text size="xs" c="dimmed">
                                    The PO quantity is the demand to meet. The velocity source is the consumption
                                    rate used for burn-down, lead-time demand and days-on-hand — <strong>Past sales</strong> uses
                                    each FC's DRR; <strong>Forecast</strong> uses the channel forecast (and any FC demand overrides).
                                </Text>
                                <SegmentedControl
                                    size="xs"
                                    data={[
                                        { label: 'Past sales (DRR)', value: 'PAST_SALES' },
                                        { label: 'Forecast', value: 'FORECAST' },
                                    ]}
                                    value={velocitySource ?? ''}
                                    onChange={v => setVelocitySource(v as DistributionDemandSource)}
                                />
                                {!velocitySource && (
                                    <Text size="xs" c="red">
                                        Pick a velocity source — PO-driven plans are blocked until this is set.
                                    </Text>
                                )}
                            </Stack>
                        </Accordion.Panel>
                    </Accordion.Item>
                )}

                {/* Target Days on Hand */}
                <Accordion.Item value="doh">
                    <Accordion.Control>
                        <Group gap="xs">
                            <Text size="sm" fw={600}>Target Days on Hand</Text>
                            <Badge size="xs" variant="light" color="violet">
                                {isAggregated
                                    ? dohMode === 'tier'
                                        ? Object.entries(tierDohMap).sort(([a], [b]) => a.localeCompare(b)).filter(([, v]) => v !== '').map(([t, v]) => `${t}:${v}d`).join(' ') || 'By tier'
                                        : targetDohDays !== '' ? `${targetDohDays}d` : 'Not set'
                                    : `${ruleGroups.length} rule${ruleGroups.length !== 1 ? 's' : ''}`}
                            </Badge>
                        </Group>
                    </Accordion.Control>
                    <Accordion.Panel>
                        <Stack gap="xs">
                            <Text size="xs" c="dimmed">
                                Stock coverage needed at the channel warehouse after the transfer arrives.
                            </Text>
                            {isAggregated ? (
                                <Stack gap="sm">
                                    <SegmentedControl
                                        value={dohMode}
                                        onChange={v => setDohMode(v as 'flat' | 'tier')}
                                        data={[
                                            { value: 'flat', label: 'All products' },
                                            { value: 'tier', label: 'By product tier' },
                                        ]}
                                        size="sm"
                                    />
                                    {dohMode === 'flat' ? (
                                        <FlatDohInput value={targetDohDays} onChange={setTargetDohDays} />
                                    ) : (
                                        <TierDohInputs
                                            tiers={orgTiers}
                                            values={tierDohMap}
                                            onChange={(tier, v) => setTierDohMap(prev => ({ ...prev, [tier]: v }))}
                                            defaultDoh={targetDohDays}
                                            onChangeDefault={setTargetDohDays}
                                        />
                                    )}
                                </Stack>
                            ) : (
                                <Stack gap="sm">
                                    <Text size="xs" c="dimmed">
                                        Create rules assigning DoH targets to warehouse groups. Each warehouse belongs to one rule.
                                    </Text>
                                    {ruleGroups.length === 0 && !addingGroup && (
                                        <Text size="sm" c="dimmed">No rules yet — add at least one.</Text>
                                    )}
                                    {ruleGroups.map((group, idx) => (
                                        <Paper key={idx} withBorder p="sm" radius="sm" style={{ background: '#fafafa' }}>
                                            {editingGroupIdx === idx ? (
                                                <Stack gap="sm">
                                                    <WhPicker
                                                        label="Warehouses"
                                                        options={warehousesForEditGroup(idx)}
                                                        value={editGroupWhs}
                                                        onChange={setEditGroupWhs}
                                                    />
                                                    <SegmentedControl
                                                        value={editGroupDohMode}
                                                        onChange={v => setEditGroupDohMode(v as 'flat' | 'tier')}
                                                        data={[{ value: 'flat', label: 'All products' }, { value: 'tier', label: 'By product tier' }]}
                                                        size="xs"
                                                    />
                                                    {editGroupDohMode === 'flat' ? (
                                                        <FlatDohInput value={editGroupDoh} onChange={setEditGroupDoh} />
                                                    ) : (
                                                        <TierDohInputs
                                                            tiers={orgTiers}
                                                            values={editGroupTierMap}
                                                            onChange={(tier, v) => setEditGroupTierMap(prev => ({ ...prev, [tier]: v }))}
                                                            defaultDoh={editGroupDoh}
                                                            onChangeDefault={setEditGroupDoh}
                                                        />
                                                    )}
                                                    {groupError && <Text size="xs" c="red">{groupError}</Text>}
                                                    <Group gap="xs" justify="flex-end">
                                                        <Button size="compact-sm" variant="subtle" onClick={() => { setEditingGroupIdx(null); setGroupError(null); }}>Cancel</Button>
                                                        <Button size="compact-sm" loading={groupSaving} disabled={editGroupWhs.length === 0} onClick={() => saveEditedGroup(idx)}>Save</Button>
                                                    </Group>
                                                </Stack>
                                            ) : (
                                                <Group justify="space-between" align="flex-start" wrap="nowrap">
                                                    <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
                                                        <Group gap={4} wrap="wrap">
                                                            {group.warehouseIds.map(id => (
                                                                <Badge key={id} size="sm" variant="light" color="blue">
                                                                    {whNameById.get(id) ?? id}
                                                                </Badge>
                                                            ))}
                                                        </Group>
                                                        <Text size="xs" c="dimmed">{formatGroupDoh(group)}</Text>
                                                    </Stack>
                                                    <Group gap="xs" style={{ flexShrink: 0 }}>
                                                        <Button size="compact-xs" variant="subtle" disabled={editingGroupIdx !== null || addingGroup} onClick={() => startEditGroup(idx)}>Edit</Button>
                                                        <Button size="compact-xs" variant="subtle" color="red" px={6} onClick={() => deleteGroup(group)}>
                                                            <IconTrash size={14} />
                                                        </Button>
                                                    </Group>
                                                </Group>
                                            )}
                                        </Paper>
                                    ))}
                                    {addingGroup ? (
                                        <Paper withBorder p="sm" radius="sm" style={{ borderStyle: 'dashed' }}>
                                            <Stack gap="sm">
                                                <WhPicker
                                                    label="Warehouses"
                                                    options={warehousesForNewGroup}
                                                    value={newGroupWhs}
                                                    onChange={setNewGroupWhs}
                                                    disabled={warehousesForNewGroup.length === 0}
                                                />
                                                <SegmentedControl
                                                    value={newGroupDohMode}
                                                    onChange={v => setNewGroupDohMode(v as 'flat' | 'tier')}
                                                    data={[{ value: 'flat', label: 'All products' }, { value: 'tier', label: 'By product tier' }]}
                                                    size="xs"
                                                />
                                                {newGroupDohMode === 'flat' ? (
                                                    <FlatDohInput value={newGroupDoh} onChange={setNewGroupDoh} />
                                                ) : (
                                                    <TierDohInputs
                                                        tiers={orgTiers}
                                                        values={newGroupTierMap}
                                                        onChange={(tier, v) => setNewGroupTierMap(prev => ({ ...prev, [tier]: v }))}
                                                        defaultDoh={newGroupDoh}
                                                        onChangeDefault={setNewGroupDoh}
                                                    />
                                                )}
                                                {groupError && <Text size="xs" c="red">{groupError}</Text>}
                                                <Group gap="xs" justify="flex-end">
                                                    <Button size="compact-sm" variant="subtle" onClick={() => { setAddingGroup(false); setGroupError(null); }}>Cancel</Button>
                                                    <Button size="compact-sm" loading={groupSaving} disabled={newGroupWhs.length === 0} onClick={saveNewGroup}>Add Rule</Button>
                                                </Group>
                                            </Stack>
                                        </Paper>
                                    ) : (
                                        warehousesForNewGroup.length > 0 && editingGroupIdx === null && (
                                            <Button size="compact-xs" variant="subtle" onClick={() => { setAddingGroup(true); setNewGroupWhs([]); setNewGroupDoh(''); setNewGroupTierMap({}); setGroupError(null); }}>
                                                + Add Rule
                                            </Button>
                                        )
                                    )}
                                </Stack>
                            )}
                        </Stack>
                    </Accordion.Panel>
                </Accordion.Item>
                {/* Source & Stock Constraints */}
                <Accordion.Item value="constraints">
                    <Accordion.Control>
                        <Group gap="xs">
                            <Text size="sm" fw={600}>Source & Stock Constraints</Text>
                            <Badge size="xs" variant="light" color="orange">
                                {[
                                    minSourceStockUnits !== '' ? `min ${minSourceStockUnits}u` : null,
                                    maxOutboundUtilizationPct !== '' ? `max ${maxOutboundUtilizationPct}%` : null,
                                    inboundInTransitDays !== '' && Number(inboundInTransitDays) > 0 ? `+${inboundInTransitDays}d in-transit` : null,
                                    sourceReserveUnits !== '' ? `reserve ${sourceReserveUnits}u` : null,
                                    minInternalDohDays !== '' ? `floor ${minInternalDohDays}d internal` : null,
                                ].filter(Boolean).join(' · ') || 'Not set'}
                            </Badge>
                        </Group>
                    </Accordion.Control>
                    <Accordion.Panel>
                        <Stack gap="sm">
                            <Text size="xs" c="dimmed">
                                Controls which SKUs are included in the plan and how much stock can be sent per run.
                            </Text>
                            <NumberInput
                                label="Min source stock (units)"
                                description="SKUs with fromWH stock below this threshold are excluded from the plan (shown as unsendable)."
                                placeholder="No minimum"
                                value={minSourceStockUnits}
                                onChange={v => setMinSourceStockUnits(v === '' ? '' : Number(v))}
                                min={0}
                                suffix=" u"
                                size="sm"
                                hideControls
                            />
                            <NumberInput
                                label="Max outbound utilisation (%)"
                                description="Maximum % of a fromWH's current stock that can be sent in a single plan run. Leave blank for no cap."
                                placeholder="No cap"
                                value={maxOutboundUtilizationPct}
                                onChange={v => setMaxOutboundUtilizationPct(v === '' ? '' : Number(v))}
                                min={1} max={100}
                                suffix="%"
                                size="sm"
                                hideControls
                            />
                            <NumberInput
                                label="Include inbound in-transit (days)"
                                description="Add inbound POs arriving at the source WH within this many days to available stock (after the utilisation cap). 0 = on-hand only."
                                placeholder="0 (on-hand only)"
                                value={inboundInTransitDays}
                                onChange={v => setInboundInTransitDays(v === '' ? '' : Number(v))}
                                min={0}
                                suffix=" d"
                                size="sm"
                                hideControls
                            />
                            <NumberInput
                                label="Source reserve (units)"
                                description="Soft reserve always kept back at each source WH — the plan sends a SKU down to this level but no further. Leave blank for no reserve."
                                placeholder="No reserve"
                                value={sourceReserveUnits}
                                onChange={v => setSourceReserveUnits(v === '' ? '' : Number(v))}
                                min={0}
                                suffix=" u"
                                size="sm"
                                hideControls
                            />
                            <NumberInput
                                label="Internal DoH floor (days)"
                                description="Cap total units leaving the internal network per SKU so internal days-on-hand (vs other channels, credited with network-wide inbound in-transit over this window) stays at/above this. Leave blank for no floor."
                                placeholder="No floor"
                                value={minInternalDohDays}
                                onChange={v => setMinInternalDohDays(v === '' ? '' : Number(v))}
                                min={0}
                                suffix=" d"
                                size="sm"
                                hideControls
                            />
                        </Stack>
                    </Accordion.Panel>
                </Accordion.Item>

                {/* Shipment Caps */}
                <Accordion.Item value="shipmentCaps">
                    <Accordion.Control>
                        <Group gap="xs">
                            <Text size="sm" fw={600}>Shipment Caps</Text>
                            <Badge size="xs" variant="light" color="cyan">
                                {[
                                    maxUnitsPerShipment !== '' ? `${maxUnitsPerShipment}u/shipment` : null,
                                    maxMeasurementPerShipment !== '' ? `${maxMeasurementPerShipment} ${measurementUnit ?? '?'}/shipment` : null,
                                ].filter(Boolean).join(' · ') || 'No caps'}
                            </Badge>
                        </Group>
                    </Accordion.Control>
                    <Accordion.Panel>
                        <Stack gap="sm">
                            <Text size="xs" c="dimmed">
                                When a shipment exceeds these caps, it is automatically split into multiple shipments at approval time.
                            </Text>
                            <NumberInput
                                label="Max units per shipment"
                                description="Total units across all SKUs in one shipment. Leave blank for no cap."
                                placeholder="No cap"
                                value={maxUnitsPerShipment}
                                onChange={v => setMaxUnitsPerShipment(v === '' ? '' : Number(v))}
                                min={1}
                                suffix=" u"
                                size="sm"
                                hideControls
                            />
                            <NumberInput
                                label="Max measurement per shipment"
                                description="Total weight or volume across all SKUs in one shipment. Leave blank for no cap."
                                placeholder="No cap"
                                value={maxMeasurementPerShipment}
                                onChange={v => setMaxMeasurementPerShipment(v === '' ? '' : Number(v))}
                                min={0}
                                size="sm"
                                hideControls
                            />
                            <Select
                                label="Measurement unit"
                                description="Shown per line on the plan (e.g. CBM next to the quantity), in shipment CSVs and emails, and used for the measurement cap when one is set. Weight uses product weight (grams); volume uses product CBM."
                                placeholder="Select unit"
                                value={measurementUnit}
                                onChange={v => setMeasurementUnit(v as 'weight' | 'volume' | null)}
                                data={[
                                    { value: 'weight', label: 'Weight (grams)' },
                                    { value: 'volume', label: 'Volume (CBM)' },
                                ]}
                                clearable
                                size="sm"
                            />
                        </Stack>
                    </Accordion.Panel>
                </Accordion.Item>
            </Accordion>

            {/* Save configuration */}
            {configError && <Text size="xs" c="red" mt="sm">{configError}</Text>}
            <Group justify="flex-end" mt="sm" mb="md">
                {configSaved ? (
                    <Button size="sm" color="green" leftSection={<IconCheck size={14} />} disabled>
                        Saved
                    </Button>
                ) : (
                    <Button size="sm" onClick={saveConfig} loading={configSaving}>
                        Save Configuration
                    </Button>
                )}
            </Group>

            {/* ── Paths ────────────────────────────────────────────────── */}
            <Stack gap="sm">
                <Group justify="space-between" align="center">
                    <Title order={6}>{isAggregated ? 'Lead Times' : 'Warehouse Paths'}</Title>
                    {pathsSaved && !editingPaths && (
                        <Button size="compact-xs" variant="subtle" onClick={() => setEditingPaths(true)}>Edit</Button>
                    )}
                </Group>

                {pathsSaved && !editingPaths ? (
                    <Stack gap={6}>
                        {pathRows.length === 0 ? (
                            <Text size="sm" c="dimmed">No paths saved.</Text>
                        ) : (
                            <>
                                <Group gap="xs" px={4}>
                                    <Text size="xs" c="dimmed" fw={500} w={180}>From</Text>
                                    {!isAggregated && <Text size="xs" c="dimmed" fw={500} w={180}>To</Text>}
                                    <Text size="xs" c="dimmed" fw={500} w={80}>Timing</Text>
                                </Group>
                                {pathRows.map((row, idx) => (
                                    <Paper key={idx} withBorder p="xs" radius="sm">
                                        <Group gap="xs" align="center">
                                            <Text size="sm" w={180} fw={500} truncate>{whNameById.get(row.fromWarehouseId) ?? row.fromWarehouseId}</Text>
                                            {!isAggregated && (
                                                <>
                                                    <Text size="xs" c="dimmed">→</Text>
                                                    <Text size="sm" w={180} truncate>{row.toWarehouseId ? (whNameById.get(row.toWarehouseId) ?? row.toWarehouseId) : '—'}</Text>
                                                </>
                                            )}
                                            {isAggregated && <Text size="xs" c="dimmed">→ {channel.channelAbbreviationKey}</Text>}
                                            <LeadTimeModeBadge mode={row.leadTimeMode} />
                                        </Group>
                                        <PathTiming suggestion={suggestions.get(routeKey(row))} row={row} prepDays={prepDays} />
                                    </Paper>
                                ))}
                            </>
                        )}
                    </Stack>
                ) : (
                    <Stack gap="sm">
                        {!isAggregated && (
                            <input ref={csvInputRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={handleCsvUpload} />
                        )}
                        <Text size="xs" c="dimmed">
                            {isAggregated
                                ? 'Lead time from each internal warehouse to this channel.'
                                : 'Allowed paths from internal warehouses to specific channel warehouses.'}
                        </Text>
                        {!isAggregated && (
                            <Text size="xs" c="dimmed">
                                CSV format: <Text span ff="monospace">from_warehouse_id, to_warehouse_id, lead_time_days</Text> — header row optional.
                            </Text>
                        )}
                        <Group justify="flex-end" gap="xs">
                            {!isAggregated && (
                                <Button size="compact-xs" variant="subtle" leftSection={<IconUpload size={12} />} onClick={() => csvInputRef.current?.click()}>Upload CSV</Button>
                            )}
                            {!addingPath && (
                                <Button size="compact-xs" variant="subtle" onClick={() => setAddingPath(true)}>+ Add Path</Button>
                            )}
                        </Group>
                        <Group gap="xs" px={4}>
                            <Text size="xs" c="dimmed" fw={500} w={180}>From</Text>
                            {!isAggregated && <Text size="xs" c="dimmed" fw={500} w={180}>To</Text>}
                            <Text size="xs" c="dimmed" fw={500} w={110}>Lead Time</Text>
                        </Group>
                        <Stack gap={6}>
                            {pathRows.map((row, idx) => (
                                <Paper key={idx} withBorder p="xs" radius="sm">
                                    <Group gap="xs" align="center">
                                        <Text size="sm" w={180} fw={500} truncate>{whNameById.get(row.fromWarehouseId) ?? row.fromWarehouseId}</Text>
                                        {!isAggregated && (
                                            <>
                                                <Text size="xs" c="dimmed">→</Text>
                                                <Text size="sm" w={180} truncate>{row.toWarehouseId ? (whNameById.get(row.toWarehouseId) ?? row.toWarehouseId) : '—'}</Text>
                                            </>
                                        )}
                                        {isAggregated && <Text size="xs" c="dimmed" w={14}>→</Text>}
                                        {isAggregated && <Text size="sm" w={180} c="dimmed">{channel.channelAbbreviationKey}</Text>}
                                        {(row.leadTimeMode === 'MANUAL' || !suggestions.get(routeKey(row))?.total || (isAggregated && row.leadTimeDays === '')) && (
                                            <NumberInput
                                                size="xs"
                                                value={row.leadTimeDays}
                                                onChange={v => setPathRows(rows => rows.map((r, i) => i === idx ? { ...r, leadTimeDays: v === '' ? '' : Number(v) } : r))}
                                                w={100} min={0} suffix="d"
                                            />
                                        )}
                                        <LeadTimeModeBadge mode={row.leadTimeMode} />
                                        <Button size="compact-xs" variant="subtle" color="red" px={6} onClick={() => deleteRow(idx)}>
                                            <IconTrash size={14} />
                                        </Button>
                                    </Group>
                                    <PathTiming suggestion={suggestions.get(routeKey(row))} row={row} prepDays={prepDays} />
                                </Paper>
                            ))}
                            {addingPath && (
                                <Paper withBorder p="xs" radius="sm">
                                    <Group gap="xs" align="flex-end">
                                        <Select label="From" size="xs" data={internalOptions} value={newRow.fromWarehouseId} onChange={v => setNewRow(r => ({ ...r, fromWarehouseId: v ?? '' }))} w={180} searchable />
                                        {!isAggregated && (
                                            <>
                                                <Text size="xs" pb={6}>→</Text>
                                                <Select label="To" size="xs" data={channelWhOptions} value={newRow.toWarehouseId} onChange={v => setNewRow(r => ({ ...r, toWarehouseId: v ?? '' }))} w={180} searchable />
                                            </>
                                        )}
                                        {isAggregated && (
                                            <>
                                                <Text size="xs" pb={6}>→</Text>
                                                <Text size="sm" w={180} pb={6} c="dimmed">{channel.channelAbbreviationKey}</Text>
                                            </>
                                        )}
                                        <NumberInput label="Lead time" size="xs" value={newRow.leadTimeDays} onChange={v => setNewRow(r => ({ ...r, leadTimeDays: v === '' ? '' : Number(v) }))} w={100} min={0} suffix="d" />
                                        <Button size="compact-sm" onClick={confirmAddRow} disabled={!newRow.fromWarehouseId}>Add</Button>
                                        <Button size="compact-sm" variant="subtle" onClick={() => setAddingPath(false)}>Cancel</Button>
                                    </Group>
                                </Paper>
                            )}
                        </Stack>
                        {pathRows.length === 0 && !addingPath && (
                            <Text size="sm" c="dimmed">No paths yet. Click "+ Add Path" or upload a CSV.</Text>
                        )}
                        {pathRows.length > 0 && (
                            <Paper withBorder p="sm" radius="sm">
                                <Stack gap="xs">
                                    <Text size="sm" fw={600}>How long stock takes to arrive</Text>
                                    <SegmentedControl
                                        size="xs"
                                        value={timingMode}
                                        onChange={v => changeTimingMode(v as DistributionLeadTimeMode)}
                                        data={[
                                            { value: 'AUTO', label: 'Auto, from history' },
                                            { value: 'MANUAL', label: 'Manual' },
                                        ]}
                                    />
                                    <Text size="xs" c="dimmed">
                                        {timingMode === 'AUTO'
                                            ? 'Each path uses the total time measured from past orders, split into process and lead where the data allows.'
                                            : 'Each path takes M1 + M2 + its own lead time.'}
                                    </Text>
                                    {timingMode === 'AUTO' && (
                                        <NumberInput
                                            label="Measure over the last"
                                            size="xs"
                                            w={200}
                                            min={7}
                                            max={180}
                                            suffix=" days"
                                            defaultValue={windowDays}
                                            key={windowDays}
                                            onBlur={e => saveWindow(Math.min(180, Math.max(7, parseInt(e.currentTarget.value, 10) || 30)))}
                                        />
                                    )}
                                    {needsProcessTime && (
                                        <>
                                            {timingMode === 'AUTO' && (
                                                <Text size="xs" c="dimmed">Used by paths with no measured total yet.</Text>
                                            )}
                                            <Group gap="md" align="flex-start">
                                                <NumberInput
                                                    label="M1 — Availability"
                                                    description="Channel confirms stock"
                                                    value={milestone1Days}
                                                    onChange={v => setMilestone1Days(v === '' ? '' : Number(v))}
                                                    min={0} max={30} suffix="d" size="sm" w={160}
                                                />
                                                <NumberInput
                                                    label="M2 — Dispatch"
                                                    description="Stock leaves your warehouse"
                                                    value={milestone2Days}
                                                    onChange={v => setMilestone2Days(v === '' ? '' : Number(v))}
                                                    min={0} max={30} suffix="d" size="sm" w={160}
                                                />
                                            </Group>
                                            {channelMeasure?.prep && (
                                                <Group gap="xs">
                                                    <Text size="xs" c="dimmed">
                                                        Suggested M1 + M2: {channelMeasure.prep.days}d from {channelMeasure.prep.sample} documents over {channelMeasure.prep.windowDays} days
                                                    </Text>
                                                    <Button
                                                        size="compact-xs"
                                                        variant="light"
                                                        onClick={() => setMilestone2Days(Math.max(channelMeasure.prep!.days - Number(milestone1Days || 0), 0))}
                                                    >
                                                        Accept into M2
                                                    </Button>
                                                </Group>
                                            )}
                                        </>
                                    )}
                                </Stack>
                            </Paper>
                        )}
                        {pathsError && <Text size="xs" c="red">{pathsError}</Text>}
                        {configError && <Text size="xs" c="red">{configError}</Text>}
                        <Group justify="flex-end" gap="xs" mt="xs">
                            {editingPaths && (
                                <Button size="sm" variant="subtle" onClick={() => { setEditingPaths(false); setPathsError(null); }}>Cancel</Button>
                            )}
                            <Button size="sm" onClick={savePaths} loading={pathsSaving}>Save Paths</Button>
                        </Group>
                    </Stack>
                )}
            </Stack>
        </Stack>
    );
};

// ─── Main tab ─────────────────────────────────────────────────────────────────

interface DistributionPathsTabProps {
    initialChannelKey?: string;
}

const IN_TRANSIT_SOURCE_OPTIONS = [
    { value: 'COMPUTED', label: 'In transit: computed' },
    { value: 'CHANNEL_SYSTEM', label: 'In transit: channel shipments' },
    { value: 'CHANNEL_STOCK_FEED', label: 'In transit: channel stock feed' },
];

const MODE_OPTIONS = [
    { label: 'Push', value: 'PUSH' },
    { label: 'PO-driven', value: 'PO_DRIVEN' },
];

const DistributionPathsTab: FC<DistributionPathsTabProps> = ({ initialChannelKey }) => {
    const [channels, setChannels] = useState<AvailableChannel[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedKey, setSelectedKey] = useState<string | null>(initialChannelKey ?? null);
    const [modeByKey, setModeByKey] = useState<Record<string, ChannelFulfillmentModeEntry>>({});
    const [savingModeKey, setSavingModeKey] = useState<string | null>(null);

    const load = () => {
        setLoading(true);
        Promise.all([getDistributionChannels(), getChannelFulfillmentModes().catch(() => [])])
            .then(([data, modes]: [AvailableChannel[], ChannelFulfillmentModeEntry[]]) => {
                setChannels(data);
                setModeByKey(Object.fromEntries(modes.map(m => [m.channelAbbreviationKey, m])));
                setSelectedKey(prev => {
                    if (prev) return prev;
                    if (initialChannelKey && data.some(c => c.channelAbbreviationKey === initialChannelKey)) return initialChannelKey;
                    return data[0]?.channelAbbreviationKey ?? null;
                });
            })
            .catch(() => setChannels([]))
            .finally(() => setLoading(false));
    };

    useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Fulfilment mode governs where the channel surfaces in the Distribution tab:
    // PUSH → Distribution (plan flow); PO_DRIVEN → PO Orders. Persisted per channel.
    const persistMode = async (
        key: string,
        updates: { fulfillmentMode?: ChannelFulfillmentMode; poActionSlaHours?: number; inTransitSource?: ChannelInTransitSource },
        patch: Partial<Pick<ChannelFulfillmentModeEntry, 'fulfillmentMode' | 'poActionSlaHours' | 'inTransitSource'>>,
    ) => {
        const previous = modeByKey[key];
        const base: ChannelFulfillmentModeEntry =
            previous ?? { channelAbbreviationKey: key, fulfillmentMode: 'PUSH', poActionSlaHours: 24 };
        setModeByKey(m => ({ ...m, [key]: { ...base, ...patch } }));
        setSavingModeKey(key);
        try {
            await setChannelFulfillmentMode(key, updates);
        } catch {
            setModeByKey(m => ({ ...m, [key]: base }));
            showError(`Could not update ${key}. Please try again.`);
        } finally {
            setSavingModeKey(null);
        }
    };

    const handleConfigSaved = (updated: AvailableChannel) => {
        setChannels(prev => prev.map(ch => ch.channelAbbreviationKey === updated.channelAbbreviationKey ? updated : ch));
    };

    const selectedChannel = channels.find(c => c.channelAbbreviationKey === selectedKey) ?? null;

    if (loading) return <Loader size="sm" />;

    if (channels.length === 0) {
        return <Text size="sm" c="dimmed">No outbound channels found. Ensure external warehouses have a channel abbreviation key set.</Text>;
    }

    return (
        <Stack gap="lg">
            <div>
                <Text size="sm" fw={500} mb="xs">Select a channel to configure</Text>
                <Group gap="xs" wrap="wrap">
                    {channels.map(ch => (
                        <Badge
                            key={ch.channelAbbreviationKey}
                            size="lg"
                            variant={selectedKey === ch.channelAbbreviationKey ? 'filled' : 'outline'}
                            color={ch.config ? 'blue' : 'gray'}
                            style={{ cursor: 'pointer' }}
                            onClick={() => setSelectedKey(k => k === ch.channelAbbreviationKey ? null : ch.channelAbbreviationKey)}
                        >
                            {ch.channelAbbreviationKey}
                            {ch.pathCount > 0 && ` · ${ch.pathCount}p`}
                        </Badge>
                    ))}
                </Group>
            </div>

            {selectedChannel && (() => {
                const modeEntry = modeByKey[selectedChannel.channelAbbreviationKey];
                const mode: ChannelFulfillmentMode = modeEntry?.fulfillmentMode ?? 'PUSH';
                const key = selectedChannel.channelAbbreviationKey;
                return (
                    <>
                        <Divider />
                        <div>
                            <Group gap="xs" mb={4}>
                                <Title order={5}>{key}</Title>
                                <Badge size="xs" variant="light" color={mode === 'PO_DRIVEN' ? 'orange' : 'grape'}>
                                    {mode === 'PO_DRIVEN' ? 'PO-driven' : 'Push'}
                                </Badge>
                                {!selectedChannel.config && (
                                    <Badge size="xs" color="gray" variant="light">Not configured</Badge>
                                )}
                            </Group>

                            {/* Fulfilment mode — decides where this channel surfaces in the
                                Distribution tab (PO Orders vs Distribution). Set first, then configure. */}
                            <Paper withBorder radius="md" p="sm" mb="md">
                                <Group justify="space-between" wrap="nowrap" align="flex-start">
                                    <Stack gap={2}>
                                        <Text fw={600} size="sm">Fulfilment mode</Text>
                                        <Text size="xs" c="dimmed">
                                            Push → Distribution (plan flow). PO-driven → PO Orders (fulfil open POs).
                                        </Text>
                                    </Stack>
                                    <Group gap={8} wrap="nowrap">
                                        {mode === 'PO_DRIVEN' && (
                                            <NumberInput
                                                size="xs"
                                                w={130}
                                                min={1}
                                                max={720}
                                                suffix="h SLA"
                                                value={modeEntry?.poActionSlaHours ?? 24}
                                                onChange={v => persistMode(key, { poActionSlaHours: Number(v) || 24 }, { poActionSlaHours: Number(v) || 24 })}
                                                disabled={savingModeKey === key}
                                            />
                                        )}
                                        <Select
                                            size="xs"
                                            w={190}
                                            aria-label="In-transit source"
                                            data={IN_TRANSIT_SOURCE_OPTIONS}
                                            value={modeEntry?.inTransitSource ?? 'COMPUTED'}
                                            allowDeselect={false}
                                            onChange={v => v && persistMode(key, { inTransitSource: v as ChannelInTransitSource }, { inTransitSource: v as ChannelInTransitSource })}
                                            disabled={savingModeKey === key}
                                        />
                                        <SegmentedControl
                                            size="xs"
                                            data={MODE_OPTIONS}
                                            value={mode}
                                            onChange={v => persistMode(key, { fulfillmentMode: v as ChannelFulfillmentMode }, { fulfillmentMode: v as ChannelFulfillmentMode })}
                                            disabled={savingModeKey === key}
                                        />
                                    </Group>
                                </Group>
                            </Paper>

                            <ChannelDetail
                                key={key}
                                channel={selectedChannel}
                                fulfillmentMode={mode}
                                onConfigSaved={handleConfigSaved}
                            />
                        </div>
                    </>
                );
            })()}
        </Stack>
    );
};

export default DistributionPathsTab;
